import { useOp } from '../../api.ts';
import { Link } from '../../router.tsx';
import type { DeeplinkCoverage as Coverage } from './types.ts';

/**
 * One line per site: of the merchants shown there whose active program is on this network, how many can get links,
 * and which miss a merchant id or the site's ad space (linking to their links page).
 */
export function DeeplinkCoverage({ networkRef, reloadKey }: { networkRef: string; reloadKey: unknown }) {
  const coverage = useOp<Coverage>('admin.deeplinks.coverage', { networkRef }, reloadKey);
  const data = coverage.data;
  if (!data) return null;
  return (
    <div className="aff-coverage" data-coverage>
      <p className="small" style={{ margin: 0 }}>
        Merchant id:{' '}
        {data.fromProgramParameters.length ? (
          <>
            <span className="badge badge-ok">from program</span> <span className="mono">{data.fromProgramParameters.join(', ')}</span>
          </>
        ) : (
          <span className="muted">stored per merchant (no parameter is filled from the program)</span>
        )}
      </p>
      {data.sites.length === 0 ? <p className="muted small" style={{ margin: 0 }}>No merchant on your sites uses this network as its active program.</p> : null}
      {data.sites.map((row) => {
        const missing = row.merchants - row.canGenerate;
        return (
          <details key={row.site.ref} className="small">
            <summary>
              <strong>{row.site.name}</strong>: {row.canGenerate} of {row.merchants} merchant(s) can get links
              {row.missingMerchantId ? <span className="text-warn"> · {row.missingMerchantId} missing a merchant id</span> : null}
              {row.missingAdSpace ? <span className="text-warn"> · ad space missing</span> : null}
              {missing > 0 && !row.missingMerchantId && !row.missingAdSpace ? <span className="text-warn"> · {missing} blocked</span> : null}
            </summary>
            {row.missing.length ? (
              <ul className="aff-coverage-list">
                {row.missing.map((m) => (
                  <li key={m.ref}>
                    <Link to={`/links?merchant=${encodeURIComponent(m.ref)}&site=${encodeURIComponent(row.site.ref)}`}>{m.name}</Link>{' '}
                    <span className="muted">needs</span> <span className="mono">{m.parameters.join(', ')}</span>
                  </li>
                ))}
                {missing > row.missing.length ? <li className="muted">and {missing - row.missing.length} more</li> : null}
              </ul>
            ) : (
              <p className="muted" style={{ margin: '4px 0' }}>Nothing missing.</p>
            )}
          </details>
        );
      })}
    </div>
  );
}
